import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { setUser, refreshUserProfile } from '../Features/User/UserSlice';

const useWebSocket = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isConnectedRef = useRef(false);

  useEffect(() => {
    // Only connect if user is logged in
    if (!user || !user._id) {
      return;
    }

    const connectWebSocket = () => {
      try {
        // Initialize socket connection
        socketRef.current = io('https://api.swasthmitra.com', {
          transports: ['websocket', 'polling'],
          timeout: 10000,
          forceNew: true
        });

        const socket = socketRef.current;

        socket.on('connect', () => {
          console.log('🔗 WebSocket connected:', socket.id);
          isConnectedRef.current = true;
          
          // Register user for real-time updates
          socket.emit('register_user', {
            userId: user._id,
            role: user.role
          });
        });

        socket.on('registration_confirmed', (data) => {
          console.log('✅ WebSocket registration confirmed:', data.message);
        });

        socket.on('registration_error', (data) => {
          console.error('❌ WebSocket registration error:', data.message);
        });

        // Listen for status updates
        socket.on('status_updated', (notification) => {
          console.log('📢 Status update received:', notification);
          
          if (notification.data && notification.data.userId === user._id) {
            // Update Redux state with new user data
            const updatedUser = {
              ...user,
              status: notification.data.status
            };
            
            dispatch(setUser(updatedUser));
            
            // Show notification to user
            if (window.Notification && Notification.permission === 'granted') {
              new Notification('Status Updated', {
                body: notification.data.message,
                icon: '/favicon.ico'
              });
            }
            
            console.log('🔄 User status updated in Redux:', notification.data.status);
          }
        });

        // Listen for redemption updates
        socket.on('redemption_updated', (notification) => {
          console.log('💰 Redemption update received:', notification);
          
          // Show notification to user
          if (window.Notification && Notification.permission === 'granted') {
            new Notification('Redemption Status Updated', {
              body: `Your redemption request has been ${notification.data.status}`,
              icon: '/favicon.ico'
            });
          }
        });

        // Listen for coin earning updates
        socket.on('coins_earned', (notification) => {
          console.log('🪙 Coin earning update received:', notification);
          
          if (notification.data && notification.data.employeeId === user._id) {
            // Dispatch action to refresh user profile to get updated coin balance
            dispatch(refreshUserProfile(user._id))
              .then(() => {
                console.log('🔄 User profile refreshed after coin earning');
              })
              .catch(error => {
                console.error('❌ Failed to refresh profile after coin earning:', error);
              });
            
            // Show notification to user
            if (window.Notification && Notification.permission === 'granted') {
              new Notification('Coins Earned! 🪙', {
                body: notification.data.message || `You earned ${notification.data.coinsEarned} coins!`,
                icon: '/favicon.ico'
              });
            }
            
            console.log('💰 Coins earned and profile will be updated:', notification.data);
          }
        });

        // Listen for token percentage updates
        socket.on('token_percentage_updated', (notification) => {
          console.log('📊 Token percentage update received:', notification);
          
          if (notification.data && notification.data.userId === user._id) {
            // Dispatch action to refresh user profile to get updated token percentage
            dispatch(refreshUserProfile(user._id))
              .then(() => {
                console.log('🔄 User profile refreshed after token percentage update');
              })
              .catch(error => {
                console.error('❌ Failed to refresh profile after token percentage update:', error);
              });
            
            // Show notification to user
            if (window.Notification && Notification.permission === 'granted') {
              new Notification('Token Percentage Updated! 📊', {
                body: notification.data.message || `Your token percentage has been updated to ${notification.data.newPercentage}%`,
                icon: '/favicon.ico'
              });
            }
            
            console.log('📈 Token percentage updated and profile will be updated:', notification.data);
          }
        });

        socket.on('disconnect', (reason) => {
          console.log('🔌 WebSocket disconnected:', reason);
          isConnectedRef.current = false;
          
          // Attempt to reconnect after delay
          if (reason === 'io server disconnect') {
            // Server initiated disconnect, reconnect manually
            reconnectTimeoutRef.current = setTimeout(() => {
              if (user && user._id) {
                connectWebSocket();
              }
            }, 3000);
          }
        });

        socket.on('connect_error', (error) => {
          console.error('🚫 WebSocket connection error:', error);
          isConnectedRef.current = false;
        });

      } catch (error) {
        console.error('🚫 WebSocket setup error:', error);
      }
    };

    // Request notification permission
    if (window.Notification && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('📱 Notification permission:', permission);
      });
    }

    // Connect WebSocket
    connectWebSocket();

    // Cleanup function
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      if (socketRef.current) {
        console.log('🔌 Cleaning up WebSocket connection');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      
      isConnectedRef.current = false;
    };
  }, [user, dispatch]);

  // Return connection status for debugging
  return {
    isConnected: isConnectedRef.current,
    socket: socketRef.current
  };
};

export default useWebSocket;